import { formatDistanceToNow } from 'date-fns'
import { ChevronRight, Clock, Code, MapPin } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link } from '#router'
import type { CoachingClient } from '../problems/types'
import { Badge } from '../shared/badge'

function formatElapsedTime(
  lastCheckInAt: Date | string | null | undefined,
): string | null {
  if (!lastCheckInAt) return null

  const checkInDate =
    typeof lastCheckInAt === 'string' ? new Date(lastCheckInAt) : lastCheckInAt
  return formatDistanceToNow(checkInDate, { addSuffix: true })
}

function getCheckInStatus(
  lastCheckInAt: Date | string | null | undefined,
  status: string,
): 'paused' | 'green' | 'yellow' | 'red' | 'neutral' {
  // Paused clients get purple
  if (status === 'paused') return 'paused'

  // No check-in yet = neutral
  if (!lastCheckInAt) return 'neutral'

  const checkInDate =
    typeof lastCheckInAt === 'string' ? new Date(lastCheckInAt) : lastCheckInAt
  const now = new Date()
  const diffMs = now.getTime() - checkInDate.getTime()
  const diffDays = diffMs / (1000 * 60 * 60 * 24)

  // Green: 0-2 days (48 hours)
  if (diffDays <= 2) return 'green'
  // Yellow: 3-4 days
  if (diffDays <= 4) return 'yellow'
  // Red: 5+ days
  return 'red'
}

const statusBackgroundClasses = {
  paused: 'bg-purple-50 dark:bg-purple-950/30',
  green: 'bg-green-50 dark:bg-green-950/30',
  yellow: 'bg-yellow-50 dark:bg-yellow-950/30',
  red: 'bg-red-50 dark:bg-red-950/30',
  neutral: 'bg-card',
}

const statusBorderClasses = {
  paused:
    'border-purple-200 dark:border-purple-800 hover:border-purple-300 dark:hover:border-purple-700',
  green:
    'border-green-200 dark:border-green-800 hover:border-green-300 dark:hover:border-green-700',
  yellow:
    'border-yellow-200 dark:border-yellow-800 hover:border-yellow-300 dark:hover:border-yellow-700',
  red: 'border-red-200 dark:border-red-800 hover:border-red-300 dark:hover:border-red-700',
  neutral: 'border-border hover:border-primary',
}

interface ClientCardProps {
  client: CoachingClient
}

export function ClientCard({ client }: ClientCardProps) {
  const {
    id,
    name,
    status,
    programmingLanguage,
    location,
    timeZone,
    lastCheckInAt,
  } = client
  const [elapsedTime, setElapsedTime] = useState<string | null>(null)
  const checkInStatus = getCheckInStatus(lastCheckInAt, status)

  useEffect(() => {
    if (!lastCheckInAt) {
      setElapsedTime(null)
      return
    }

    const updateElapsed = () => {
      setElapsedTime(formatElapsedTime(lastCheckInAt))
    }

    updateElapsed()
    const interval = setInterval(updateElapsed, 60_000) // Update every minute

    return () => clearInterval(interval)
  }, [lastCheckInAt])

  return (
    <Link
      href={`/admin/coaching/client/${id}`}
      className={`group block rounded-lg border p-4 transition-all hover:bg-muted/50 ${statusBackgroundClasses[checkInStatus]} ${statusBorderClasses[checkInStatus]}`}
    >
      <div className="flex items-center justify-between">
        <div className="min-w-0 flex-1">
          <div className="mb-2 flex items-center gap-3">
            <h3 className="text-foreground group-hover:text-primary truncate text-lg font-semibold transition-colors">
              {name}
            </h3>
            <Badge variant={status.toLowerCase()}>{status.toLowerCase()}</Badge>
          </div>

          <div className="text-muted-foreground flex flex-wrap items-center gap-4 text-sm">
            {programmingLanguage && (
              <div className="flex items-center gap-1.5">
                <Code size={14} className="text-muted-foreground/60" />
                <span>{programmingLanguage}</span>
              </div>
            )}
            {location && (
              <div className="flex items-center gap-1.5">
                <MapPin size={14} className="text-muted-foreground/60" />
                <span>{location}</span>
              </div>
            )}
            {timeZone && (
              <div className="flex items-center gap-1.5">
                <Clock size={14} className="text-muted-foreground/60" />
                <span>{timeZone}</span>
              </div>
            )}
            {elapsedTime && (
              <div className="flex items-center gap-1.5">
                <Clock size={14} className="text-muted-foreground/60" />
                <span className="font-mono text-xs">Last: {elapsedTime}</span>
              </div>
            )}
          </div>
        </div>

        <ChevronRight
          size={20}
          className="text-muted-foreground group-hover:text-primary ml-4 flex-shrink-0 transition-colors"
        />
      </div>
    </Link>
  )
}
